/**
 * TodaysMeetingsStrip — M-Rhythm-2 / M-Rhythm-3
 *
 * Small horizontal list of today's meetings displayed on the Control Tower
 * dashboard above the work-item views. Fetches from GET /api/v1/meetings/today.
 *
 * Each item: time + title + status chip — click opens MeetingDetailReadOnlyModal.
 * Empty state: "No meetings today" message.
 *
 * M-Rhythm-3:
 *   - CB-001: Import statusVariant/statusLabel from shared utils
 *   - CB-006: "View calendar" uses <Button variant="link"> not a raw <button>
 *   - Error state with Retry button
 *
 * Uses boilerplate LoadingState for loading feedback.
 * Does NOT affect WIP capacity (meetings != work items).
 *
 * Compliance: M-Rhythm-2 plan §5 task 9–10 / M-Rhythm-3 CB-001 + CB-006.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { useRorosTodaysMeetings } from '@/hooks/roros/meetings';
import type { CalendarMeetingDTO, MeetingStatus } from '@/types/roros/meetings';
import { MeetingDetailReadOnlyModal } from './MeetingDetailReadOnlyModal';
import { statusVariant, statusLabel } from './meeting-status-utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatShortTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Meeting item ─────────────────────────────────────────────────────────────

function MeetingItem({ meeting, onOpen }: { meeting: CalendarMeetingDTO; onOpen: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2 h-auto text-left text-sm',
        'border border-border bg-card hover:bg-muted/50',
        'min-w-0 shrink-0 justify-start',
      )}
      aria-label={meeting.title}
      data-testid={`todays-meeting-${meeting.id ?? meeting.cadenceScheduleId}`}
    >
      <span className="text-xs font-mono text-muted-foreground shrink-0">
        {formatShortTime(meeting.scheduledAt)}
      </span>
      <span className="font-medium truncate max-w-[140px]">{meeting.title}</span>
      <Badge variant={statusVariant(meeting.status)} className="shrink-0 text-[10px] px-1.5 py-0.5">
        {statusLabel(meeting.status as MeetingStatus)}
      </Badge>
    </Button>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TodaysMeetingsStrip() {
  const navigate = useNavigate();
  const { data: meetings, isLoading, isError, refetch } = useRorosTodaysMeetings();
  const [selectedMeeting, setSelectedMeeting] = useState<CalendarMeetingDTO | null>(null);

  return (
    <>
      <div
        className="mb-4 flex-shrink-0"
        data-testid="todays-meetings-strip"
        aria-label={t("Today's meetings")}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t("Today's meetings")}
            </span>
          </div>
          {/* CB-006: Use <Button variant="link"> instead of raw <button> for navigation */}
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate('/roros/rhythm/calendar')}
            className="text-xs h-auto p-0"
          >
            {t('View calendar')}
          </Button>
        </div>

        {isLoading ? (
          <LoadingState size="sm" />
        ) : isError ? (
          <div className="flex items-center gap-2 py-1">
            <p className="text-xs text-destructive">{t('Failed to load meetings')}</p>
            <Button variant="link" size="sm" onClick={() => refetch()} className="text-xs h-auto p-0">
              {t('Retry')}
            </Button>
          </div>
        ) : !meetings || meetings.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">{t('No meetings today')}</p>
        ) : (
          <div className="flex items-start gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {meetings.map((m, idx) => (
              <MeetingItem
                key={m.id ?? `virtual-${m.cadenceScheduleId}-${idx}`}
                meeting={m}
                onOpen={() => setSelectedMeeting(m)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedMeeting && (
        <MeetingDetailReadOnlyModal
          meeting={selectedMeeting}
          isOpen={!!selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
        />
      )}
    </>
  );
}
